import React from 'react';
import classNames from 'classnames';

import {
  useDarkWidgets,
  useTheme,
  useLegalInfo,
  useI18n
} from 'pageflow-scrolled/frontend';

import {Logo} from './Logo';
import {SocialIcon} from './SocialIcon';
import {socialPlatforms} from './socialPlatforms';

import styles from './LinkListFooter.module.css';

export function LinkListFooter() {
  const theme = useTheme();
  const darkWidgets = useDarkWidgets();

  const legalInfo = useLegalInfo();
  const {t} = useI18n();

  const groups = (theme.options.footerLinkGroups || legacyGroups(theme.options.footerLinks))
    .map(group => ({
      ...group,
      links: (group.links || [])
        .filter(item => !item.hidden)
        .map(item => resolveItem(item, legalInfo))
        .filter(item => item.kind === 'privacySettings' || item.label)
    }))
    .filter(group => group.links.length);

  if (!theme.options.displayFooter) {
    return null;
  }

  function renderLinkOrButton(item) {
    if (item.kind === 'privacySettings') {
      return (
        <button className={styles.link}
                onClick={() => window.pageflowDisplayPrivacySettings() &&
                             window.pageflowDisplayPrivacySettings()}>
          {t('pageflow_scrolled.public.privacy_settings')}
        </button>
      );
    }

    if (item.kind === 'social') {
      return (
        <a className={classNames(styles.link, styles.socialLink)}
           href={item.url}>
          <SocialIcon platform={item.platform} />
          <span dangerouslySetInnerHTML={{__html: item.label}} />
        </a>
      );
    }

    return (
      <a className={styles.link}
         href={item.url}
         dangerouslySetInnerHTML={{__html: item.label}} />
    );
  }
  return (
    <footer className={classNames(styles.bar,
                                  styles[`layout-${theme.options.linkListFooterLayout || 'logoAbove'}`],
                                  styles[`arrangement-${theme.options.linkListFooterGroupArrangement || 'rows'}`],
                                  styles[`spacing-${theme.options.linkListFooterSpacing || 'tight'}`],
                                  {'scope-dark': darkWidgets})}
            style={columnCountStyle(groups)}>
      <Logo />

      <div className={styles.content}>
        <div className={styles.groups}>
          {groups.map((group, index) =>
            <div key={index} className={styles.group}>
              {group.title && <h2 className={styles.title}>{group.title}</h2>}
              <ul className={styles.items}>
                {group.links.map((item, index) =>
                  <li key={index} className={styles.item}>
                    {renderLinkOrButton(item)}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {theme.options.footerText &&
         <p className={styles.text}
            dangerouslySetInnerHTML={{__html: theme.options.footerText}} />}
      </div>
    </footer>
  );
}

function legacyGroups(footerLinks) {
  return [{links: footerLinks}];
}

// Fill the fewest rows possible given at most four columns, then
// balance columns across those rows (e.g. six groups render as two
// rows of three).
function columnCountStyle(groups) {
  if (!groups.length) {
    return undefined;
  }

  const rows = Math.ceil(groups.length / 4);

  return {'--link-list-footer-column-count': Math.ceil(groups.length / rows)};
}

function resolveItem(item, legalInfo) {
  if (legalInfo[item.kind]) {
    return {...legalInfo[item.kind], kind: item.kind};
  }

  if (item.kind === 'social') {
    return {...item, label: item.label || socialPlatforms[item.platform]};
  }

  return item;
}
